import { useState, useRef, useEffect } from "react";
import {
  ChevronDownIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";

import { standardRefKey } from "../utilities/recordDiff";
import { standardRefPropType } from "../utilities/recordPropTypes";

// Standard ID of a reference (handles populated objects and IDs)
const getStandardId = (ref) => {
  const standard = ref.standard;
  const id = standard && typeof standard === "object" ? standard._id : standard;
  return id ? id.toString() : "";
};

// Same order as Admin → Standards and GET /standards (MongoDB sort { sortOrder: 1, name: 1 }):
// sortOrder, then name by plain character code; unknown standards last
const compareStandards = (a, b) => {
  if (!a || !b) return (a ? 0 : 1) - (b ? 0 : 1);
  const bySortOrder = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  if (bySortOrder !== 0) return bySortOrder;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
};

const inputClass =
  "border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:border-cyan-600";

/**
 * Dropdown showing a field's references to other fund data standards,
 * grouped by standard. Editable unless readOnly.
 *
 * @param {Object} props
 * @param {Array} props.standardRefs - References ({ standard, externalId, comment }), standard populated or ID
 * @param {Array} props.standards - Selectable standards ({ _id, name, sortOrder }); the name includes the version; also used to label references
 * @param {Function} props.onChange - Callback with the new references array
 * @param {Array} props.originalRefs - If given, added/changed references are marked red and removed ones struck through
 * @param {string} props.bgColor - Background color class of the button
 * @param {boolean} props.readOnly - If true, references are only displayed
 */
export default function StandardRefsDropdown({
  standardRefs,
  standards = [],
  onChange = () => {},
  originalRefs = null,
  bgColor = "bg-gray-200",
  readOnly = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Standards by ID, from the selectable list and from populated references
  const standardsById = {};
  for (const ref of [...(originalRefs || []), ...standardRefs]) {
    if (ref.standard && typeof ref.standard === "object") {
      standardsById[getStandardId(ref)] = ref.standard;
    }
  }
  for (const standard of standards) {
    standardsById[standard._id] = standard;
  }

  // Diff against the original references
  const currentKeys = new Set(standardRefs.map(standardRefKey));
  const originalKeys = new Set((originalRefs || []).map(standardRefKey));
  const isChanged = (ref) => !!originalRefs && !originalKeys.has(standardRefKey(ref));
  const removedRefs = (originalRefs || []).filter(
    (ref) => !currentKeys.has(standardRefKey(ref)),
  );

  // Group references (and removed ones) by standard, ordered like the standards
  const groupsById = {};
  const groupFor = (standardId) =>
    (groupsById[standardId] ??= {
      standardId,
      standard: standardsById[standardId],
      items: [],
      removed: [],
    });
  standardRefs.forEach((ref, index) =>
    groupFor(getStandardId(ref)).items.push({ ref, index }),
  );
  removedRefs.forEach((ref) => groupFor(getStandardId(ref)).removed.push(ref));
  const groups = Object.values(groupsById).sort((a, b) =>
    compareStandards(a.standard, b.standard),
  );

  const standardLabel = (standard) => standard?.name ?? "Unknown standard";

  const updateRef = (index, key, value) =>
    onChange(
      standardRefs.map((ref, i) => (i === index ? { ...ref, [key]: value } : ref)),
    );
  const removeRef = (index) =>
    onChange(standardRefs.filter((_, i) => i !== index));
  const addRef = (standardId) => {
    if (standardId) onChange([...standardRefs, { standard: standardId }]);
  };

  // Button label: names of the referenced standards and the reference count
  const referencedNames = groups
    .filter((group) => group.items.length > 0)
    .map((group) => group.standard?.name ?? "Unknown");
  const buttonLabel = referencedNames.length
    ? `${referencedNames.join(", ")} (${standardRefs.length})`
    : "No references";

  // One line per reference: external ID, then the comment beside it.
  // Read-only: a row of the panel's grid (subgrid), so all comments start right
  // after the longest external ID. Edit mode (removed refs): a flex row.
  const renderReadOnlyRef = (ref, className, key) =>
    readOnly ? (
      <div key={key} className={`col-span-full grid grid-cols-subgrid text-sm ${className}`}>
        <div className="min-w-0 font-semibold [overflow-wrap:anywhere]">
          {ref.externalId}
        </div>
        <div className="min-w-0 break-words opacity-80">{ref.comment}</div>
      </div>
    ) : (
      <div key={key} className={`flex gap-2 text-sm ${className}`}>
        <div className="flex-1 min-w-0 px-2 font-semibold break-words">
          {ref.externalId}
        </div>
        <div className="w-28 sm:w-48 flex-shrink-0 px-2 break-words opacity-80">
          {ref.comment}
        </div>
        {/* Space of the remove button, to line up with the input rows */}
        <div className="w-5 flex-shrink-0" />
      </div>
    );

  const sortedStandards = [...standards].sort(compareStandards);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center min-h-12 px-4 py-3 text-base border border-gray-700 rounded transition-all duration-200 ${bgColor} text-gray-700 w-full justify-between`}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDownIcon
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 mt-2 w-full max-h-[32rem] overflow-y-auto bg-white border border-gray-700 rounded shadow-lg z-50 ${
            readOnly ? "grid grid-cols-[fit-content(70%)_minmax(0,1fr)] gap-x-6" : ""
          }`}
        >
          {groups.length === 0 && (
            <div className="col-span-full px-4 py-3 text-sm text-gray-500">
              No references to other standards.
            </div>
          )}
          {groups.map((group) => (
            <div
              key={group.standardId}
              className={`px-4 py-3 border-b border-gray-200 last:border-b-0 ${
                readOnly ? "col-span-full grid grid-cols-subgrid" : ""
              }`}
            >
              <div className="col-span-full flex items-center justify-between mb-2">
                <span className="text-sm font-bold uppercase tracking-wide text-cyan-900">
                  {standardLabel(group.standard)}
                </span>
                {!readOnly && group.standard && (
                  <button
                    type="button"
                    onClick={() => addRef(group.standardId)}
                    title={`Add another ${group.standard.name} reference`}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-700"
                  >
                    <PlusIcon className="w-4 h-4" /> Add
                  </button>
                )}
              </div>
              <div
                className={
                  readOnly
                    ? "col-span-full grid grid-cols-subgrid gap-y-2"
                    : "flex flex-col gap-2"
                }
              >
                {group.items.map(({ ref, index }) => {
                  if (readOnly) {
                    return renderReadOnlyRef(
                      ref,
                      isChanged(ref) ? "text-red-600 font-semibold" : "text-gray-700",
                      index,
                    );
                  }
                  const textClass = isChanged(ref) ? "text-red-600" : "text-gray-700";
                  return (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="External ID / path"
                        value={ref.externalId ?? ""}
                        onChange={(e) => updateRef(index, "externalId", e.target.value)}
                        className={`${inputClass} ${textClass} flex-1 min-w-0`}
                      />
                      <input
                        type="text"
                        placeholder="Comment"
                        value={ref.comment ?? ""}
                        onChange={(e) => updateRef(index, "comment", e.target.value)}
                        className={`${inputClass} ${textClass} w-28 sm:w-48 flex-shrink-0`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRef(index)}
                        title="Remove this reference"
                        className="w-5 flex-shrink-0 text-gray-400 hover:text-red-600"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
                {group.removed.map((ref) =>
                  renderReadOnlyRef(
                    ref,
                    "text-red-600 line-through",
                    standardRefKey(ref),
                  ),
                )}
              </div>
            </div>
          ))}
          {!readOnly && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              {sortedStandards.length ? (
                <select
                  value=""
                  onChange={(e) => addRef(e.target.value)}
                  className={`${inputClass} text-gray-700`}
                >
                  <option value="">+ Add reference to…</option>
                  {sortedStandards.map((standard) => (
                    <option key={standard._id} value={standard._id}>
                      {standardLabel(standard)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-gray-500">
                  No standards defined yet. Add them in Admin → Standards.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

StandardRefsDropdown.propTypes = {
  standardRefs: PropTypes.arrayOf(standardRefPropType).isRequired,
  standards: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      sortOrder: PropTypes.number,
    }),
  ),
  onChange: PropTypes.func,
  originalRefs: PropTypes.arrayOf(standardRefPropType),
  bgColor: PropTypes.string,
  readOnly: PropTypes.bool,
};
